import { useEffect, useState } from "react";
import { History, FileDown, GitCompare } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../ui/dialog";
import { Section, NativeSelect, MarginBadge, Empty } from "../common";
import DiffView from "./DiffView";
import { api, errMsg, downloadPdf, useFetch } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { useT } from "../../lib/i18n";
import { money, fdt } from "../../lib/format";

export default function RevisionsTab({ q, locked, reload, showCost }) {
  const { t } = useT();
  const { can } = useAuth();
  const { data: revs, reload: reloadRevs } = useFetch(`/quotations/${q.id}/revisions`);
  const [a, setA] = useState("");
  const [b, setB] = useState("draft");
  const [diff, setDiff] = useState(null);
  const [restoreRev, setRestoreRev] = useState(null);
  const [reason, setReason] = useState("");
  useEffect(() => {
    if (revs?.length && a === "") setA(String(revs[revs.length > 1 ? 1 : 0].rev));
    if (revs?.length > 1 && b === "draft" && a === "") setB(String(revs[0].rev));
  }, [revs, a, b]);
  const compare = async () => {
    try { const r = await api.get(`/quotations/${q.id}/diff?a=${a}&b=${b}`); setDiff(r.data); } catch (e) { toast.error(errMsg(e)); }
  };
  const doRestore = async () => {
    try {
      const r = await api.post(`/quotations/${q.id}/revisions/${restoreRev}/restore`, { reason });
      toast.success(`Rev ${r.data.rev} ← Rev ${r.data.restored_from}`);
      setRestoreRev(null); setReason(""); await reload(); reloadRevs();
    } catch (e) { toast.error(errMsg(e)); }
  };
  if (!revs) return <Empty>{t("common.loading")}</Empty>;
  const opts = revs.map((r) => ({ value: String(r.rev), label: `Rev ${r.rev} · ${fdt(r.created_at)}` }));
  return (
    <div className="space-y-4">
      <Section title={t("ws.revList")} testid="section-revisions">
        {!revs.length ? <Empty>{t("ws.noRevisions")}</Empty> : (
          <div className="overflow-x-auto">
            <table className="dense-table w-full" data-testid="revisions-table">
              <thead><tr><th>Rev</th><th>{t("common.date")}</th><th>{t("ws.by")}</th><th>{t("ws.reason")}</th><th className="text-right">{t("ws.total")}</th>
                {showCost && <th>{t("q.margin")}</th>}<th>{t("ws.hash")}</th><th>{t("common.actions")}</th></tr></thead>
              <tbody>
                {revs.map((r) => (
                  <tr key={r.rev} data-testid={`revision-row-${r.rev}`}>
                    <td><span className="rounded-sm bg-black px-1.5 py-0.5 font-mono text-[11px] font-bold text-white">Rev {r.rev}</span>
                      {r.sent && <span className="ml-1 text-[10px] font-semibold text-sky-700">{t("ws.sent")}</span>}
                      {r.restored_from != null && <div className="mt-1 text-[10px] text-amber-700">{t("ws.restoredFrom")} {r.restored_from}</div>}</td>
                    <td className="whitespace-nowrap">{fdt(r.created_at)}</td>
                    <td>{r.created_by_name}</td>
                    <td className="max-w-[240px] truncate text-neutral-600">{r.reason}</td>
                    <td className="num">{money(r.totals?.total)}</td>
                    {showCost && <td><MarginBadge value={r.totals?.margin_pct} /></td>}
                    <td className="font-mono text-[10px] text-neutral-500" title={r.pdf_sha256}>{r.pdf_sha256?.slice(0, 12)}…</td>
                    <td className="whitespace-nowrap">
                      <Button size="sm" variant="ghost" data-testid={`revision-pdf-${r.rev}`} onClick={() => downloadPdf(`/quotations/${q.id}/revisions/${r.rev}/pdf`, `${q.folio}-Rev${r.rev}.pdf`).catch((e) => toast.error(errMsg(e)))}><FileDown className="h-4 w-4" />PDF</Button>
                      {!locked && can("quote:publish") && <Button size="sm" variant="ghost" data-testid={`revision-restore-${r.rev}`} onClick={() => setRestoreRev(r.rev)}><History className="h-4 w-4" />{t("ws.restore")}</Button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>
      {revs.length > 0 && (
        <Section title={t("ws.compare")} testid="section-compare" right={
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs">{t("ws.from")}</span>
            <div className="w-full sm:w-56"><NativeSelect testid="diff-from-select" value={a} onChange={(v) => setA(v)} options={opts} /></div>
            <span className="text-xs">{t("ws.to")}</span>
            <div className="w-full sm:w-56"><NativeSelect testid="diff-to-select" value={b} onChange={(v) => setB(v)} options={[{ value: "draft", label: t("ws.draftCurrent") }, ...opts]} /></div>
            <Button size="sm" data-testid="diff-compare-btn" onClick={compare}><GitCompare className="h-4 w-4" />{t("ws.compare")}</Button>
          </div>}>
          {diff ? <DiffView diff={diff} currency={q.currency} /> : <Empty>—</Empty>}
        </Section>
      )}
      <Dialog open={restoreRev != null} onOpenChange={() => setRestoreRev(null)}>
        <DialogContent data-testid="restore-dialog">
          <DialogHeader><DialogTitle>{t("ws.restore")} Rev {restoreRev}</DialogTitle><DialogDescription>{t("ws.restoreConfirm")}</DialogDescription></DialogHeader>
          <Textarea data-testid="restore-reason-input" placeholder={t("ws.reason")} value={reason} onChange={(e) => setReason(e.target.value)} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRestoreRev(null)}>{t("common.cancel")}</Button>
            <Button data-testid="restore-confirm-btn" onClick={doRestore}>{t("ws.restore")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
